import { ref, Ref, computed, onMounted, watch } from 'vue'

import { useIO } from '@/composables/io'

import { useDataStore } from "@/stores/data"

import { DefinitionListContentColumn } from '@/components/types'

import HclListHeaderColumnBody from '@/components/ListHeaderColumnBody/ListHeaderColumnBody.vue'

interface ColumnSortingItem {
  type: string
  name: string
  sortColumn: string
  sortDirection: string
  items: ColumnSortingItem[]
}

export function useColumns(parentId: string, columns: DefinitionListContentColumn[], config: any, pager: any): any {

  const columnsRef: Ref<DefinitionListContentColumn[]> = ref(columns)

  const columnHeaderIsMoving: Ref<boolean> = ref(false)

  const columnHeaderOnDragStart = () => {
    columnHeaderIsMoving.value = true
  }

  const columnHeaderOnDragMove = (event) => {
    columnHeaderIsMoving.value = false

    /* we can not move a column onto a unmoveable column */
    try {
      return event.relatedContext.element.moveable
    } catch (e) {
      return false
    }
  }

  const columnHeaderOnDragEnd = () => {
    columnHeaderIsMoving.value = false

    storeColumnsLocalStorage()
  }

  const columnVisible = (column: DefinitionListContentColumn) => {
    if (column.type === "stack" && column.items.length > 0) {
      return !column.items.every((column) => {
        return column.hidden
      })
    }

    return !column.hidden
  }

  const columnsSortingRef: Ref<ColumnSortingItem[]> = ref([])

  const columnSorting = (column: DefinitionListContentColumn) => {
    for (const columnSortingRef of columnsSortingRef.value) {
      if (columnSortingRef.name === column.name) {
        return columnSortingRef
      }
    }

    return {}
  }

  const columnHeaderComponentName = () => {
    return HclListHeaderColumnBody
  }

  const parentIdRef: Ref<string> = ref(parentId)

  const columnHeaderOnSortChange = (event) => {
    onColumnSortChangeWithId(event, parentId)
  }

  const columnsVisible = computed(() => {
    const result: DefinitionListContentColumn[] = []

    for (const column of columnsRef.value) {
      if (column.type === "stack" && column.items.length > 0) {
        const t: DefinitionListContentColumn[] = []
        for (const item of column.items) {
          if (!item.hidden) {
            t.push(item)
          }
        }
        if (t.length > 0) {
          result.push({ ...column, items: t })
        }
      } else {
        if (!column.hidden) {
          result.push(column)
        }
      }
    }

    return result
  })

  const columnsVisibleCount = computed(() => {
    return columnsVisible.value.length
  })

  const storeColumnsLocalStorage = () => {
    const storageKey = `hcl-list-result-columns-${parentId}`
    localStorage.setItem(storageKey, JSON.stringify(columnsRef.value))
  }

  const moveColumn = (columnsRef, o: number, n: number) => {
    if (n >= columnsRef.value.length) {
      let k = n - columnsRef.value.length
      while (k--) {
        columnsRef.value.push(undefined)
      }
    }
    columnsRef.value.splice(n, 0, columnsRef.value.splice(o, 1)[0])
  }

  const findColumn = (columnsRef: Ref<DefinitionListContentColumn[]>, name: string) => {
    for (const column of columnsRef.value) {
      if (column.name === name) {
        return column
      }
    }
  }

  const updateStackItems = (currentColumn: DefinitionListContentColumn, storageColumn: DefinitionListContentColumn) => {
    for (let actualItem of currentColumn.items) {
      for (let storageItem of storageColumn.items) {
        if (actualItem.name === storageItem.name) {
          actualItem.hidden = storageItem.hidden
        }
      }
    }
  }

  const updateColumn = (currentRef: Ref<DefinitionListContentColumn[]>, storageRef: Ref<DefinitionListContentColumn[]>, columnName: string) => {
    let currentColumn = findColumn(currentRef, columnName)
    let storageColumn = findColumn(storageRef, columnName)

    if (currentColumn !== undefined && storageColumn !== undefined) {
      currentColumn.hidden = storageColumn.hidden
      if (currentColumn.type === "stack" && storageColumn.type === "stack") {
        updateStackItems(currentColumn, storageColumn)
      }
    }
  }

  /**
   * Restore the list table columns, given a columns arrangement retrieved
   * from local storage.
   *
   * Since restoring from a stored column arrangement can be quite tricky
   * given the multitude of column operations, i.e. columns can be added,
   * columns can be removed, columns can be moved to a different position,
   * columns can be made hidden, made immoveable, renamed etc. we need to take
   * an iterative approach.
   *
   * Important to remember, only the columns as such are important (we can
   * disregard the items in a stack) and column names are unique.
   */
  const restoreColumns = (currentRef: Ref<DefinitionListContentColumn[]>, storageRef: Ref<DefinitionListContentColumn[]>) => {

    // Array with only the top level Column Names of the current Columns Definition
    const currentColumnsNames = currentRef.value.map((column: DefinitionListContentColumn) => column.name)

    // Copy to work with
    const currentColumnsNamesCopy = [...currentColumnsNames]

    // Array with only Column Names of the Columns Definition retrieved from Browser Storage
    const storageColumnsNames = storageRef.value.map((column: DefinitionListContentColumn) => column.name)

    // Copy to work with, but with any columns which are in storage but not in current filtered out
    const storageColumnsNamesCopy = storageColumnsNames.filter((name: string) => currentColumnsNames.includes(name))

    let currentIndex = 0
    while (storageColumnsNamesCopy.length > 0 && currentColumnsNamesCopy.length > 0) {
      let current0Name = currentColumnsNamesCopy[0]
      let storage0Name = storageColumnsNamesCopy[0]

      // if the first element of current and storage pertain to the same column,
      // update its configuration, remove it from both lists of column names.
      // We remember the index where they both came from.
      if (current0Name === storage0Name) {
        updateColumn(currentRef, storageRef, storage0Name)
        storageColumnsNamesCopy.shift()
        currentColumnsNamesCopy.shift()
        currentIndex++
        continue
      }

      let currentIndexMovedColumnName = currentColumnsNames.indexOf(storage0Name)
      moveColumn(currentRef, currentIndexMovedColumnName, currentIndex)
      updateColumn(currentRef, storageRef, storage0Name)
      storageColumnsNamesCopy.shift()
      let currentIndexMovedColumNameCopy = currentColumnsNamesCopy.indexOf(storage0Name)
      currentColumnsNamesCopy.splice(currentIndexMovedColumNameCopy, 1)
      currentIndex++
    }

    storeColumnsLocalStorage()
  }

  const jsonParse = (s: string) => {
    let result: any

    try {
      result = JSON.parse(s)
    } catch (e) {
      result = undefined
    }

    return result;
  }

  const fetchColumnsLocalStorage = () => {
    const storageKeyLegacy = `${parentId}-columns`
    const sl = localStorage.getItem(storageKeyLegacy)
    if (sl) {
      const json = jsonParse(sl)
      if (json) {
        restoreColumns(columnsRef, ref(json))
        storeColumnsLocalStorage()
        localStorage.removeItem(storageKeyLegacy)
        return
      }
    }

    const storageKey = `hcl-list-result-columns-${parentId}`
    const s = localStorage.getItem(storageKey)
    if (s) {
      const json = jsonParse(s)
      if (json) {
        restoreColumns(columnsRef, ref('value' in json ? json.value : json))
      }
    }
  }

  onMounted(() => {
    fetchColumnsLocalStorage()
  })

  /**
   * Sorting...
   */
  const { performPostBucket } = useIO()

  const performSortAjax = async (event, pager, id) => {
    let sort: string

    if (event.sort === "asc") {
      sort = "desc"
    } else if (event.sort === "desc") {
      sort = ""
    } else {
      sort = "asc"
    }

    const pageStart = (pager.pageCurrent - 1) * pager.rowsPerPage
    const pageEnd = pager.pageCurrent * pager.rowsPerPage - 1

    const data = {
      source: {
        id: id
      },
      pagination: {
        rowsPerPage: pager.rowsPerPage,
        page: pager.pageCurrent,
        pageStart: pageStart,
        pageEnd: pageEnd
      },
      sorting: {
        sortColumn: event.name,
        sortDirection: sort
      },
      values: pager.criteria
    }

    const url = config.sorting.ajax

    performPostBucket(
      url,
      {},
      {
        ...data
      },
      '',
      () => { }
    )
  }

  const onColumnSortChangeWithId = (event, id) => {
    if (config.sorting.type === "esearcher" && (config.sorting.mode !== "ajax" && config.sorting.mode !== "ajax-legacy")) {
      return
    }

    if (config.sorting.type === "esearcher" && (config.sorting.mode === "ajax" || config.sorting.mode === "ajax-legacy")) {
      performSortAjax(event, pager, id)
    }

    let match = { sort: "asc" }

    for (const column of columnsRef.value) {
      if ("items" in column) {
        for (const item of column.items) {
          if (item.name === event.name) {
            match = item
          }
        }
      }

      if (column.name === event.name) {
        match = event
      }
    }

    if (match.sort === "asc") {
      match.sort = "desc"
    } else if (match.sort === "desc") {
      match.sort = ""
    } else {
      match.sort = "asc"
    }
  }

  const initializeColumnsSortingRef = () => {
    columnsSortingRef.value.slice(0, 0)

    for (const column of columnsRef.value) {
      if (column.type === "stack") {
        const i: any[] = []
        for (const item of column.items) {
          i.push({
            type: item.type,
            name: item.name,
            sortColumn: item.name,
            sortDirection: item.sort
          })
        }
        columnsSortingRef.value.push({
          type: column.type,
          name: column.name,
          sortColumn: '',
          sortDirection: '',
          items: i
        })
      } else {
        columnsSortingRef.value.push({
          type: column.type,
          name: column.name,
          sortColumn: column.name,
          sortDirection: column.sort,
          items: []
        })
      }
    }
  }

  initializeColumnsSortingRef()

  const { link } = useDataStore()

  const updateColumnsSortingRef = (bucketSorting: any) => {
    if (bucketSorting === undefined) return

    for (const columnSortingRef of columnsSortingRef.value) {
      if (columnSortingRef.type === "stack") {
        for (const item of columnSortingRef.items) {
          if (item.sortColumn === bucketSorting.sortColumn) {
            item.sortDirection = bucketSorting.sortDirection
          } else {
            item.sortDirection = ''
          }
        }
      } else {
        if (columnSortingRef.sortColumn === bucketSorting.sortColumn) {
          columnSortingRef.sortDirection = bucketSorting.sortDirection
        } else {
          columnSortingRef.sortDirection = ''
        }
      }
    }
  }

  const bucket = link(parentIdRef.value)

  watch(
    bucket,
    (n: any) => {
      updateColumnsSortingRef(bucket.value.sorting)
    },
    { deep: true }
  )

  return {
    columnsRef,
    columnsSortingRef,
    columnHeaderOnDragStart,
    columnHeaderOnDragMove,
    columnHeaderOnDragEnd,
    columnVisible,
    columnSorting,
    columnHeaderComponentName,
    columnHeaderOnSortChange,
    columnsVisibleCount
  }
}
